<template>
    <div class="marketplace-page">
        <!-- Credit Score Dialog - Conservé tel quel -->
           <v-dialog v-model="showCreditScoreDialog" max-width="800px">
      <v-card style="border-radius: 12px; overflow: hidden;">
        <v-card-title class="teal darken-2 white--text" style="background: linear-gradient(135deg, #00695C 0%, #004D40 100%);">
          <v-icon left color="white">mdi-star-circle</v-icon>
          <span class="headline font-weight-bold">Understanding Your Credit Score</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="showCreditScoreDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4" style="max-height: 600px; overflow-y: auto;">
          <div class="credit-score-explanation">
            <div class="mb-4">
              <h3 class="teal--text text--darken-2 mb-2">🌟 What is a Credit Score?</h3>
              <p class="body-2">
                Your <strong>Credit Score</strong> serves as a tangible digital guarantee within the PettyCash ecosystem.
                It enables Business Suite clients to access credit facilities from PettyCash SAS, specifically in the form
                of goods and services, using their score as leverage rather than traditional cash deposits.
              </p>
              <div class="mt-3 pa-3" style="background: #e3f2fd; border-radius: 8px; border-left: 4px solid #2196F3;">
                <p class="body-2 mb-0">
                  <strong>💡 Key Concept:</strong> Your Credit Score represents your financial reliability and unlocks
                  opportunities without requiring upfront cash deposits. The higher your score, the more financial
                  flexibility you gain.
                </p>
              </div>
            </div>

            <div class="mb-4">
              <h3 class="teal--text text--darken-2 mb-2">📈 Credit Accumulation Protocol</h3>
              <p class="body-2 mb-3">
                Your Credit Score grows through two distinct channels: <strong>Personal Status</strong> (your individual actions)
                and <strong>Network Engagement</strong> (activities of your referred users).
              </p>

              <div class="mb-3">
                <h4 class="teal--text text--darken-1 mb-2" style="font-size: 1rem;">
                  <v-icon small color="teal" class="mr-1">mdi-account-star</v-icon>
                  Personal Status Points
                </h4>
                <p class="body-2 caption grey--text mb-2">Earn points through your own activities and milestones</p>
                <v-list dense class="elevation-1" style="border-radius: 8px;">
                  <v-list-item>
                    <v-list-item-icon class="mr-2">
                      <v-icon small color="green">mdi-check-circle</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="body-2">Business Account Validation: <strong>+10 points</strong> when approved</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon class="mr-2">
                      <v-icon small color="green">mdi-check-circle</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="body-2">On-time Campaign Completion: Bonus points based on performance</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>

              <div class="mb-3">
                <h4 class="teal--text text--darken-1 mb-2" style="font-size: 1rem;">
                  <v-icon small color="teal" class="mr-1">mdi-account-group</v-icon>
                  Network Engagement Points
                </h4>
                <p class="body-2 caption grey--text mb-2">Earn points when your referred users take action</p>
                <v-list dense class="elevation-1" style="border-radius: 8px;">
                  <v-list-item>
                    <v-list-item-icon class="mr-2">
                      <v-icon small color="orange">mdi-star-circle</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="body-2">Successful Referral: <strong>+5 points</strong> when a user joins through your link</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon class="mr-2">
                      <v-icon small color="orange">mdi-star-circle</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="body-2">Referred User Njangi Contribution: <strong>+5 points</strong> per validated contribution</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon class="mr-2">
                      <v-icon small color="orange">mdi-star-circle</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="body-2">Referred User Campaign Completion: <strong>+5 points</strong> for each completed campaign</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>

              <div class="mt-3 pa-3" style="background: #fff3e0; border-radius: 8px; border-left: 4px solid #FF9800;">
                <p class="body-2 mb-0">
                  <strong>🎯 Pro Tip:</strong> Build your network strategically! Each active referred user multiplies
                  your earning potential through their ongoing activities.
                </p>
              </div>
            </div>

            <div class="mb-4">
              <h3 class="teal--text text--darken-2 mb-2">🚚 Credit Score Utility: Early Delivery at 50%</h3>
              <div class="early-delivery-info">
                <p class="body-2 mb-3">
                  Your accumulated Credit Score unlocks a powerful benefit: the ability to request delivery of marketplace items
                  when your campaign reaches just <strong>50%</strong> of its target amount. This feature transforms your Credit Score
                  from a simple metric into <strong>tangible financial leverage</strong>.
                </p>

                <h4 class="teal--text text--darken-2 mb-2">📋 Eligibility Requirements</h4>
                <div class="requirements-list mb-3">
                  <div class="requirement-item">
                    <v-icon small color="teal" class="mr-2">mdi-check-circle</v-icon>
                    <span class="body-2">Valid Business Account with confirmed payment status</span>
                  </div>
                  <div class="requirement-item">
                    <v-icon small color="teal" class="mr-2">mdi-check-circle</v-icon>
                    <span class="body-2">Campaign progress at minimum 50% completion</span>
                  </div>
                  <div class="requirement-item">
                    <v-icon small color="teal" class="mr-2">mdi-check-circle</v-icon>
                    <span class="body-2">Sufficient Credit Score based on product price bracket</span>
                  </div>
                </div>

                <div class="credit-brackets-section">
                  <h4 class="teal--text text--darken-2 mb-2">📊 Credit Brackets by Product Price</h4>
                  <p class="body-2 mb-3">
                    The required credit depends on the price bracket of your product. Higher priced items require more credit.
                  </p>


                  <div class="example-scenario mt-3">
                    <h5 class="teal--text text--darken-2 mb-1">📋 Example Scenario:</h5>
                    <p class="body-2">
                      If you're saving for a product priced at <strong>75,000 FCFA</strong>:
                    </p>
                    <ul class="example-steps">
                      <li class="body-2">1. Your campaign needs to reach <strong>37,500 FCFA</strong> (50%)</li>
                      <li class="body-2">2. You need a valid Business Account</li>
                      <li class="body-2">3. You need at least the <strong>minimum credit points required for this product’s bracket</strong>.</li>
                      <li class="body-2">4. Once all conditions are met, you can request early delivery!</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div class="mb-4">
              <h3 class="teal--text text--darken-2 mb-2">💎 Benefits of a Good Credit Score</h3>
              <div class="benefits-grid">
                <div class="benefit-item">
                  <v-icon small color="teal" class="mr-1">mdi-truck-fast</v-icon>
                  <span class="caption">Early Delivery at 50%</span>
                </div>
                <div class="benefit-item">
                  <v-icon small color="teal" class="mr-1">mdi-shield-check</v-icon>
                  <span class="caption">Higher Trust Level</span>
                </div>
                <div class="benefit-item">
                  <v-icon small color="teal" class="mr-1">mdi-percent</v-icon>
                  <span class="caption">Better Loan Terms</span>
                </div>
                <div class="benefit-item">
                  <v-icon small color="teal" class="mr-1">mdi-star</v-icon>
                  <span class="caption">Priority Support</span>
                </div>
              </div>
            </div>



            <div class="mt-4 pt-3" style="border-top: 1px solid #e0e0e0;">
              <p class="caption grey--text">
                <v-icon x-small color="grey" class="mr-1">mdi-information</v-icon>
                Your credit score is updated in real-time. Maintain good financial habits to improve your score!
              </p>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="pa-4" style="background: #f5f5f5;">


          <v-btn
            color="teal darken-2"
            dark
            @click="showCreditScoreDialog = false"
            style="border-radius: 24px;"
          >
            <v-icon left small>mdi-check</v-icon>
            Got it!
          </v-btn>
        </v-card-actions>
      </v-card>
        </v-dialog>

        <!-- Hero Section améliorée comme le premier design -->
        <div class="hero-section primary darken-2">
            <div class="hero-gradient"></div>
            <div class="hero-pattern"></div>

            <v-container class="hero-content">
                <!-- Welcome Badge comme le premier design -->
                <div class="welcome-badge" v-if="$store.state.isAuthenticated">
                    <span class="badge-icon">😃</span>
                    <span class="badge-text">Welcome <strong class="notranslate username-text" translate="no">{{$store.state.user_info["user"]["username"]}}</strong>, to PettyCash: MarketPlace</span>
                </div>
                <div class="welcome-badge" v-else>
                    <span class="badge-icon">😃</span>
                    <span class="badge-text">Welcome to <strong>PettyCash: MarketPlace</strong></span>
                </div>

                <!-- Credit Score Badge comme le premier design -->
                <div class="credit-score-badge" @click="showCreditScoreDialog = true">
                    <v-icon small class="credit-icon">mdi-star-circle</v-icon>
                    <span class="credit-text">Your Credit Score: <strong>{{ userCreditScore }}</strong></span>
                    <v-icon small class="info-icon">mdi-information-outline</v-icon>
                </div>

                <h1 class="hero-title">
                    Shop at cheap prices/credit at our
                    <span class="hero-title-accent">market place.</span>
                </h1>

                <!-- Carousel intégré dans le Hero Section -->
                <div class="carousel-container">
                    <v-carousel
                        :height="currentSlideHeight2"
                        hide-delimiters
                        :show-arrows="false"
                        cycle
                        interval="4000"
                        class="header-carousel"
                        v-model="currentSlideIndex"
                        @change="onSlideChange"
                    >
                        <!-- SLIDE 0: Message de bienvenue (similaire au premier design) -->
                        <v-carousel-item>
                            <div class="carousel-welcome-slide" :style="{ height: currentSlideHeight2 + 'px' }">
                                <!-- Animated Journey Illustration comme le premier design -->
<v-layout row wrap class="journey-flow">
    <v-flex wrap xs2 sm2 md2 lg2 class="journey-item" style="animation-delay: 0.1s">
        <v-btn outline fab color="white" class="outine-2 journey-btn">
            <i class="fas fa-coins fa-3x"></i>
        </v-btn>
    </v-flex>

    <v-flex wrap xs2 sm2 md2 lg2 class="journey-item" style="animation-delay: 0.2s">
        <v-btn flat fab color="white" class="journey-btn-flat">
            <i class="fas fa-plus fa-2x"></i>
        </v-btn>
    </v-flex>

    <v-flex wrap xs2 sm2 md2 lg2 class="journey-item" style="animation-delay: 0.3s">
        <v-btn outline fab color="white" class="outine-2 journey-btn">
            <i class="fas fa-hand-holding-usd fa-2x"></i>
        </v-btn>
    </v-flex>

    <v-flex wrap xs2 sm2 md2 lg2 class="journey-item" style="animation-delay: 0.4s">
        <v-btn flat fab color="white" class="journey-btn-flat">
            <i class="fas fa-arrow-right fa-2x"></i>
        </v-btn>
    </v-flex>

    <v-flex xs2 sm2 md3 lg3 class="journey-item fortune-item" style="animation-delay: 0.5s">
        <div class="fortune-glow"></div>
        <v-img
            :src="require('@/assets/market_place_items.png')"
            aspect-ratio="2"
            contain
            height="90"
            width="120"
            class="fortune-image"
        ></v-img>
    </v-flex>
</v-layout>

                            </div>
                        </v-carousel-item>

                        <!-- SLIDES 1+: Articles en vedette (conservés de votre design original) -->
                        <v-carousel-item v-for="item in carouselProducts" :key="'carousel-' + item.id">
                            <div class="article-slide-overlay" @click="goToItem(item.id)" :style="{ height: currentSlideHeight2 + 'px' }">
                                <!-- Mêmes éléments que votre design original -->
                                <div class="media-container-overlay animated-media">
                                    <v-img
                                        v-if="!isVideo(item.get_image)"
                                        :src="item.get_image"
                                        class="article-background-image"
                                    ></v-img>
                                    <video
                                        v-else
                                        :src="item.get_image"
                                        class="article-background-video"
                                        muted
                                        loop
                                        autoplay
                                    ></video>

                                    <div class="image-overlay animated-overlay"></div>
                                </div>

                                <div class="overlay-content animated-content">
                                    <div class="article-badge">⭐ MOST BOUGHT</div>
                                    <h2 class="article-title">{{ item.name }}</h2>
                                    <p class="article-description">{{ item.description | truncate(80, '...') }}</p>

                                    <div class="article-details">
                                        <div class="price-tag">
                                            <span class="price-label">Price:</span>
                                            <span class="price-value">{{ item.price }} FCFA</span>
                                        </div>
                                        <div class="stock-info">
                                            <v-icon small color="white">check_circle</v-icon>
                                            <span>{{ item.quantity }} in stock</span>
                                        </div>
                                    </div>

                                    <v-btn color="white" class="primary--text mt-3" round depressed>
                                        <v-icon left small>shopping_cart</v-icon>
                                        View Details
                                    </v-btn>
                                </div>
                            </div>
                        </v-carousel-item>
                    </v-carousel>
                </div>
            </v-container>
        </div>

        <!-- Main Content -->
        <v-container class="main-content mt-5">
            <v-layout row>
                <v-flex xs12 md10 offset-md1 lg10 offset-lg1>
                    <v-card flat class="card--flex-toolbar" color="transparent">
                        <v-container fluid grid-list-lg>
                            <!-- Section Header -->
                            <v-layout row wrap>
                                <v-flex xs12 class="mt-0 pt-0 mb-2">
                                    <h3 class="white--text section-title">Explore Petty Ca$h Market Place</h3>
                                </v-flex>
                            </v-layout>

                            <!-- Section des catégories (conservée de votre design) -->
                            <v-layout row wrap class="mb-4">
                                <v-flex xs4 sm4 md4 class="px-2 mb-3">
                                    <v-hover v-slot:default="{ hover }">
                                        <v-card
                                            :class="`elevation-${hover ? 8 : 2} category-card-pro`"
                                            :to="getCategoryLink(5)"
                                            class="category-business-pro"
                                        >
                                            <div class="category-gradient business-gradient" :class="{ 'active': hover }"></div>
                                            <div class="category-content-pro">
                                                <div class="icon-wrapper-pro" :class="{ 'hover': hover }">
                                                    <i class="fas fa-briefcase category-icon-pro"></i>
                                                </div>
                                                <div class="category-text-pro">
                                                    <div class="category-title-pro">Business</div>
                                                    <div class="category-subtitle-pro">Suite</div>
                                                </div>
                                                <v-icon class="category-arrow-pro" :class="{ 'visible': hover }">
                                                    arrow_forward
                                                </v-icon>
                                            </div>
                                            <div class="category-accent business-accent" :class="{ 'expanded': hover }"></div>
                                        </v-card>
                                    </v-hover>
                                </v-flex>

                                <v-flex xs4 sm4 md4 class="px-2 mb-3">
                                    <v-hover v-slot:default="{ hover }">
                                        <v-card
                                            :class="`elevation-${hover ? 8 : 2} category-card-pro`"
                                            :to="getCategoryLink(2)"
                                            class="category-electronic-pro"
                                        >
                                            <div class="category-gradient electronic-gradient" :class="{ 'active': hover }"></div>
                                            <div class="category-content-pro">
                                                <div class="icon-wrapper-pro" :class="{ 'hover': hover }">
                                                    <i class="fas fa-laptop category-icon-pro"></i>
                                                </div>
                                                <div class="category-text-pro">
                                                    <div class="category-title-pro">Electronics</div>
                                                    <div class="category-subtitle-pro">Devices</div>
                                                </div>
                                                <v-icon class="category-arrow-pro" :class="{ 'visible': hover }">
                                                    arrow_forward
                                                </v-icon>
                                            </div>
                                            <div class="category-accent electronic-accent" :class="{ 'expanded': hover }"></div>
                                        </v-card>
                                    </v-hover>
                                </v-flex>

                                <v-flex xs4 sm4 md4 class="px-2 mb-3">
                                    <v-hover v-slot:default="{ hover }">
                                        <v-card
                                            :class="`elevation-${hover ? 8 : 2} category-card-pro`"
                                            to="/market_place/bills/payment"
                                            class="category-bills-pro"
                                        >
                                            <div class="category-gradient bills-gradient" :class="{ 'active': hover }"></div>
                                            <div class="category-content-pro">
                                                <div class="icon-wrapper-pro" :class="{ 'hover': hover }">
                                                    <i class="fas fa-file-invoice-dollar category-icon-pro"></i>
                                                </div>
                                                <div class="category-text-pro">
                                                    <div class="category-title-pro">Bills</div>
                                                    <div class="category-subtitle-pro">Services</div>
                                                </div>
                                                <v-icon class="category-arrow-pro" :class="{ 'visible': hover }">
                                                    arrow_forward
                                                </v-icon>
                                            </div>
                                            <div class="category-accent bills-accent" :class="{ 'expanded': hover }"></div>
                                        </v-card>
                                    </v-hover>
                                </v-flex>

                                <!-- Lien "Explore All" -->
                                <v-flex xs12 class="text-xs-center mt-3">
                                    <v-btn
                                        color="primary"
                                        :to="'/market_place/items/types/all'"
                                        class="explore-all-btn"
                                        depressed
                                        round>
                                        <span class="mr-2">Explore All Categories</span>
                                        <v-icon size="18">arrow_forward</v-icon>
                                    </v-btn>
                                </v-flex>
                            </v-layout>

                            <!-- Cards Grid amélioré comme le premier design -->
                            <v-layout row wrap>
                                <v-flex
                                    xs12 sm6 md6 lg4
                                    v-for="item in links"
                                    :key="item.id"
                                    class="pa-2"
                                >
                                    <v-hover v-slot="{ hover }">
                                        <v-card
                                            :class="['service-card', `elevation-${hover ? 12 : 4}`]"
                                            style="border-radius: 5px; font-size: small"
                                            :to="item.link"
                                        >
                                            <!-- Accent Border on Hover -->
                                            <div class="card-accent-border" :style="{ background: getAccentColor(item.color) }"></div>

                                            <!-- Gradient Overlay -->
                                            <div class="card-gradient" :style="{ background: getCardGradient(item.color) }"></div>

                                            <!-- Card Header -->
                                            <v-layout row>
                                                <v-flex xs8 sm8 md8>
                                                    <v-btn
                                                        fab
                                                        :color="item.color"
                                                        :class="['mt-2 ml-3 mb-0 mr-2 white--text icon-btn', item.color]"
                                                    >
                                                        <i :class="item.icon" class="icon-display"></i>
                                                    </v-btn>
                                                </v-flex>

                                                <!-- Menu -->
                                                <v-flex xs4 sm4 md4 v-if="!(item.title == 'Legal Bindings')" class="text-xs-right">
                                                    <v-menu bottom right>
                                                        <template v-slot:activator="{ on }">
                                                            <v-btn
                                                                icon
                                                                flat
                                                                fab
                                                                class="menu-btn mt-2 ml-3 mb-0"
                                                                v-on="on"
                                                                @click.prevent="activateMenu(item.title)"
                                                            >
                                                                <i class="fa-solid fa-ellipsis-vertical fa-2x"></i>
                                                            </v-btn>
                                                        </template>
                                                        <v-list class="grey lighten-4 menu-list">
                                                            <v-list-tile
                                                                v-for="menuLink in menuItems"
                                                                :key="menuLink.title"
                                                                :to="menuLink.link"
                                                                class="menu-item"
                                                            >
                                                                <v-list-tile-title>{{ menuLink.title }}</v-list-tile-title>
                                                            </v-list-tile>
                                                        </v-list>
                                                    </v-menu>
                                                </v-flex>
                                            </v-layout>

                                            <!-- Card Content -->
                                            <v-card-title primary-title class="pb-0 mt-0 pt-1" style="color: #424242">
                                                <div class="card-content">
                                                    <h5 class="subheading pl-0 pb-0 mb-0 card-title"><b>{{ item.title }}</b></h5>
                                                    <div class="caption mt-0 mb-0 pb-2 card-description">{{ item.description }}</div>

                                                    <div class="card-footer">
                                                        <span class="card-link primary--text text--darken-2">
                                                            Explore
                                                            <i class="fas fa-arrow-right card-arrow-icon"></i>
                                                        </span>
                                                    </div>
                                                </div>
                                            </v-card-title>
                                        </v-card>
                                    </v-hover>
                                </v-flex>
                            </v-layout>

                            <!-- Section Most Bought Items (conservée) -->
                            <h2 class="ma-3 underline">Most Bought Items</h2>
                            <v-layout row wrap>
                                <v-flex xs12 sm6 v-for="item in mostBoughtItems" :key="item.id">
                                    <v-hover>
                                        <v-card color="white" slot-scope="{ hover }" :class=" `ma-2 elevation-${hover ? 12 : 2}`"  :to="'/market_place/items/'+item.id" style="border-radius: 10px; overflow: hidden;">
                                            <v-container fluid class="pa-0">
                                                <v-layout row wrap class="ma-0">
                                                    <!-- Section image améliorée avec la même hauteur -->
                                                    <v-flex xs12 sm5 md4 class="pa-0 ma-0">
                                                        <div class="media-container" style="position: relative; width: 100%; height: 200px; background: #f8f8f8; overflow: hidden;">
                                                            <!-- Affichage vidéo -->
                                                            <video
                                                                v-if="isVideo(item.get_image)"
                                                                :src="item.get_image"
                                                                style="width: 100%; height: 100%; object-fit: cover;"
                                                                controls
                                                                preload="metadata"
                                                                @mouseenter="playVideo"
                                                                @mouseleave="pauseVideo"
                                                            >
                                                                Your browser does not support the video tag.
                                                            </video>

                                                            <!-- Affichage image amélioré -->
                                                            <v-img
                                                                v-else
                                                                :src="item.get_image"
                                                                :lazy-src="item.get_image"
                                                                height="200px"
                                                                width="100%"
                                                                style="transition: transform 0.4s ease;"
                                                                :style="hover ? 'transform: scale(1.08)' : ''"
                                                                cover
                                                            >
                                                                <template v-slot:placeholder>
                                                                    <v-layout fill-height align-center justify-center ma-0>
                                                                        <v-progress-circular indeterminate color="teal"></v-progress-circular>
                                                                    </v-layout>
                                                                </template>
                                                            </v-img>

                                                            <!-- Overlay amélioré -->
                                                            <div
                                                                v-if="!isVideo(item.get_image) && hover"
                                                                style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; transition: all 0.3s; opacity: 1;"
                                                            >
                                                                <v-icon size="48" color="white" style="text-shadow: 0 2px 4px rgba(0,0,0,0.5);">zoom_in</v-icon>
                                                            </div>
                                                        </div>
                                                    </v-flex>

                                                    <!-- Section informations conservée -->
                                                    <v-flex xs12 sm7 md8 class="ma-0 pa-3">
                                                        <div class="ml-1">
                                                            <div class="title ma-0 pa-0 text-xs-left"><u>{{item.name| truncate(23, '...') }}</u></div>
                                                            <div>
                                                                <v-layout row wrap class="ma-0 pa-0">
                                                                    <v-flex xs12 text-xs-left class="ma-0 pt-2 pb-0 px-0">
                                                                        <span class="dark--text font-weight-bold">Description:</span> {{ item.description| truncate(55, '...')  }}
                                                                    </v-flex>
                                                                    <v-flex xs8 text-xs-left class="ma-0 pa-0">
                                                                        <span class="dark--text font-weight-bold">Cost:</span><span> {{item.price}} FCFA</span>
                                                                    </v-flex>
                                                                    <v-flex xs4 text-xs-right class="ma-0 pa-0">
                                                                        <span class="dark--text font-weight-bold">Qty:</span><span> {{item.quantity}}</span>
                                                                    </v-flex>
                                                                    <v-flex xs6 text-xs-left class="ma-0 pa-0">
                                                                        <span class="dark--text font-weight-bold">In-Stock:</span><span> {{item.quantity}}</span>
                                                                    </v-flex>
                                                                    <v-flex xs6 text-xs-right class="ma-0 pa-0">
                                                                        <span class="dark--text font-weight-bold">Type:</span><span> {{item.category}}</span>
                                                                    </v-flex>
                                                                </v-layout>
                                                            </div>
                                                        </div>
                                                    </v-flex>
                                                </v-layout>
                                            </v-container>
                                        </v-card>
                                    </v-hover>
                                </v-flex>
                                <v-flex xs12 class="text-xs-right mt-3">
                                    <v-btn class="blue elevation-6" round color="primary" to="/market_place/items/types/all" >
                                        <span> View All Items </span>
                                    </v-btn>
                                </v-flex>
                            </v-layout>
                        </v-container>
                    </v-card>
                </v-flex>
            </v-layout>
        </v-container>
    </div>
</template>

<script>
import axios from "axios";

export default {
    data() {
        return {
            userCreditScore: 0,
            showCreditScoreDialog: false,
            currentSlideHeight: 210,
            currentSlideHeight2: 210,
            currentSlideIndex: 0,
            carouselProducts: [],
            mostBoughtItems: [],
            menuItems: [],
            activeCategory: null,
            links: [
                {
                    id: '6',
                    title: 'Start Purchase Campaign',
                    description: 'Launch a savings campaign to buy an article through installment payments',
                    color: 'red',
                    icon: 'fa-solid fa-rocket fa-2x',
                    link: '/market_place/create-campaigns',
                    add_link: false
                },
                {
                    id: '7',
                    title: 'My Purchase Campaign',
                    description: 'Track the evolution of your created campaigns and make savings contributions',
                    color: 'purple',
                    icon: 'fa-solid fa-chart-line fa-2x',
                    link: '/market_place/campaign/active',
                    add_link: false
                },
                {
                    id: '2',
                    title: 'Shopping Bag',
                    description: 'View the items you have selected and want to purchase.',
                    color: 'orange',
                    icon: 'fa-solid fa-shopping-basket fa-2x',
                    link: '/market_place/shopping_bag/items/transactions',
                    add_link: false
                },
                {
                    id: '3',
                    title: 'Shopping History',
                    description: 'View all your previous transactions made, items bought and favorite items.',
                    color: 'blue',
                    icon: 'fa-solid fa-history fa-2x',
                    link: '/market_place/history/items/types/favorite',
                    add_link: false
                },
                {
                    id: '4',
                    title: 'Contact Us',
                    description: 'Get in contact with us if you have any worries/suggestions concerning our services',
                    color: 'green',
                    icon: 'fa-solid fa-address-card fa-2x',
                    link: '/market_place/contact_us/all',
                    add_link: false
                },
                {
                    id: '5',
                    title: 'Legal Bindings',
                    description: 'Learn of the rules, legal and social rights that protect you as you shop with Petty Cash',
                    color: 'purple',
                    icon: 'fa-solid fa-file-contract fa-2x',
                    link: '/market_place/legal_binding/all',
                    add_link: false
                },
            ],
        };
    },
    mounted() {
        document.title = "PettyCash | Market Place";
        this.getMostBoughtItems();
        this.getCarouselProducts();
        this.getUserCreditScore();
    },
    methods: {
        async getUserCreditScore() {
            try {
                if (this.$store.state.user_info && this.$store.state.user_info.user_credit && this.$store.state.user_info.user_credit[0]) {
                    this.userCreditScore = this.$store.state.user_info.user_credit[0].credit || 0;
                } else {
                    const response = await axios.get('/api/v1/home/get/user/info/');
                    if (response.data[0] && response.data[0].user_credit && response.data[0].user_credit[0]) {
                        this.userCreditScore = response.data[0].user_credit[0].credit || 0;
                    }
                }
            } catch (error) {
                console.error('Error fetching credit score:', error);
                this.userCreditScore = 0;
            }
        },
        onSlideChange(slideIndex) {
            this.currentSlideIndex = slideIndex;
            if (slideIndex === 0) {
                this.currentSlideHeight = 210;
                this.currentSlideHeight2 = 210;
            } else {
                this.currentSlideHeight = 400;
                this.currentSlideHeight2 = 380;
            }
        },
        getCategoryLink(categoryId) {
            return `/market_place/items/types/all?category=${categoryId}`;
        },
        playVideo(event) {
            if (event.target.tagName === 'VIDEO') {
                event.target.play().catch(() => {});
            }
        },
        pauseVideo(event) {
            if (event.target.tagName === 'VIDEO') {
                event.target.pause();
            }
        },
        activateMenu(title) {
            if (title == "Start Purchase Campaign") {
                this.menuItems = [
                    { "title": "Create Campaign", "link": "/market_place/campaign/create/new_campaign" },
                    { "title": "Campaign Guide", "link": "/market_place/create-campaigns" },
                    { "title": "Active Campaigns", "link": "/market_place/campaign/active" },
                ];
            }
            else if (title == "My Purchase Campaign") {
                this.menuItems = [
                    { "title": "Active", "link": "/market_place/campaign/active" },
                    { "title": "Completed", "link": "/market_place/campaign/completed" },
                    { "title": "Interrupted", "link": "/market_place/campaign/interrupted" },
                    { "title": "Create New", "link": "/market_place/campaign/create/new_campaign" },
                ];
            }
            else if (title == "Shopping Bag") {
                this.menuItems = [
                    { "title": "My Items", "link": "/market_place/shopping_bag/items/my_items" },
                    { "title": "Transactions", "link": "/market_place/shopping_bag/items/transactions" },
                    { "title": "Billing Info", "link": "/market_place/shopping_bag/items/billing_info" },
                ];
            }
            else if (title == "Shopping History") {
                this.menuItems = [
                    { "title": "Favorite", "link": "/market_place/history/items/types/favorite" },
                    { "title": "Recent", "link": "/market_place/history/items/types/recent" },
                    { "title": "All", "link": "/market_place/history/items/types/all" },
                ];
            }
            else if (title == "Contact Us") {
                this.menuItems = [
                    { "title": "All Chats", "link": "/market_place/contact_us/all" },
                    { "title": "Create New Chat", "link": "/market_place/contact_us/new_ticket" },
                ];
            }
        },
        goToItem(itemId) {
            this.$router.push(`/market_place/items/${itemId}`);
        },
        isVideo(url) {
            if (!url) return false;
            const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
            return videoExtensions.some(ext => url.toLowerCase().includes(ext));
        },
        async getCarouselProducts() {
            this.$store.commit('setIsLoading', true);
            await axios
                .get('/api/v1/market_place/get/random_products/')
                .then(response => {
                    this.carouselProducts = response.data;
                })
                .catch(error => {
                    if (error.response) {
                        this.$store.commit('setSnackBarMessage', error.response.status + " " + error.response.statusText);
                        this.$store.commit('setSnackBarColor', "red darken-4");
                        this.$store.commit('activateSnackBar', true);
                    } else {
                        this.$store.commit('setSnackBarMessage', "An error occured, please check your internet connection and try again later.");
                        this.$store.commit('setSnackBarColor', "red darken-4");
                        this.$store.commit('activateSnackBar', true);
                    }
                });
            this.$store.commit('setIsLoading', false);
        },
        async getMostBoughtItems() {
            this.$store.commit('setIsLoading', true);
            await axios
                .get('/api/v1/market_place/get/latest_products/best/info/')
                .then(response => {
                    this.mostBoughtItems = response.data;
                })
                .catch(error => {
                    if (error.response) {
                        this.$store.commit('setSnackBarMessage', error.response.status + " " + error.response.statusText);
                        this.$store.commit('setSnackBarColor', "red darken-4");
                        this.$store.commit('activateSnackBar', true);
                    } else {
                        this.$store.commit('setSnackBarMessage', "An error occured, please check your internet connection and try again later.");
                        this.$store.commit('setSnackBarColor', "red darken-4");
                        this.$store.commit('activateSnackBar', true);
                    }
                });
            this.$store.commit('setIsLoading', false);
        },
        // Méthodes du premier design
        getCardGradient(color) {
            const gradients = {
                red: 'linear-gradient(135deg, rgba(244, 67, 54, 0.04) 0%, rgba(244, 67, 54, 0) 100%)',
                purple: 'linear-gradient(135deg, rgba(156, 39, 176, 0.04) 0%, rgba(156, 39, 176, 0) 100%)',
                orange: 'linear-gradient(135deg, rgba(255, 152, 0, 0.04) 0%, rgba(255, 152, 0, 0) 100%)',
                blue: 'linear-gradient(135deg, rgba(33, 150, 243, 0.04) 0%, rgba(33, 150, 243, 0) 100%)',
                green: 'linear-gradient(135deg, rgba(76, 175, 80, 0.04) 0%, rgba(76, 175, 80, 0) 100%)',
            };
            return gradients[color] || gradients.purple;
        },
        getAccentColor(color) {
            const accents = {
                red: '#F44336',
                purple: '#9C27B0',
                orange: '#FF9800',
                blue: '#2196F3',
                green: '#4CAF50',
            };
            return accents[color] || accents.purple;
        }
    },
};
</script>

<style scoped>
/* Styles copiés du premier design */
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;700&display=swap');

.marketplace-page {
    min-height: 100vh;
    background: #f5f5f5;
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ===== HERO SECTION (copié du premier design) ===== */
.hero-section {
    position: relative;
    padding: 40px 0;
    min-height: 420px;
    overflow: hidden;
}

.hero-gradient {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
        radial-gradient(circle at 20% 50%, rgba(156, 39, 176, 0.12) 0%, transparent 50%),
        radial-gradient(circle at 80% 30%, rgba(255, 152, 0, 0.08) 0%, transparent 50%);
    pointer-events: none;
}

.hero-pattern {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
        radial-gradient(circle at 20px 20px, rgba(255, 255, 255, 0.02) 2px, transparent 0);
    background-size: 40px 40px;
    pointer-events: none;
}

.hero-content {
    position: relative;
    z-index: 1;
    text-align: center;
}

.welcome-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    padding: 8px 16px;
    border-radius: 50px;
    color: white;
    font-size: 14px;
    margin-bottom: 12px;
    animation: slideInDown 0.6s ease-out;
}

.badge-icon {
    font-size: 16px;
}

.badge-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.badge-text strong {
    font-weight: 700;
}

/* Credit Score Badge (copié du premier design) */
.credit-score-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(255, 165, 0, 0.2));
    backdrop-filter: blur(10px);
    border: 2px solid rgba(255, 215, 0, 0.5);
    padding: 10px 20px;
    border-radius: 50px;
    color: white;
    font-size: 15px;
    margin-bottom: 16px;
    cursor: pointer;
    transition: all 0.3s ease;
    animation: pulseGlow 2s infinite, slideInDown 0.6s ease-out 0.1s both;
}

.credit-score-badge:hover {
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.3), rgba(255, 165, 0, 0.3));
    transform: scale(1.05);
    box-shadow: 0 0 25px rgba(255, 215, 0, 0.6);
}

.credit-icon {
    color: #FFD700;
    font-size: 20px !important;
}

.credit-text {
    font-weight: 500;
}

.credit-text strong {
    font-weight: 700;
    color: #FFD700;
}

.info-icon {
    color: #FFD700;
    font-size: 18px !important;
}

@keyframes pulseGlow {
    0%, 100% {
        box-shadow: 0 0 10px rgba(255, 215, 0, 0.4);
    }
    50% {
        box-shadow: 0 0 20px rgba(255, 215, 0, 0.8), 0 0 30px rgba(255, 165, 0, 0.5);
    }
}

.hero-title {
    font-family: 'Poppins', sans-serif;
    font-size: clamp(20px, 4vw, 28px);
    font-weight: 600;
    color: white;
    line-height: 1.3;
    margin-bottom: 32px;
    animation: slideInDown 0.6s ease-out 0.2s both;
    padding: 0 16px;
}

.hero-title-accent {
    display: block;
    background: linear-gradient(135deg, #FFD54F 0%, #FF9800 50%, #FF6F00 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-top: 4px;
    font-weight: 700;
}

/* ===== JOURNEY FLOW (copié du premier design) ===== */
.journey-flow {
    animation: fadeInUp 0.6s ease-out 0.3s both;
    justify-content: center;
    align-items: center;
}

.journey-item {
    display: flex;
    justify-content: center;
    align-items: center;
}

.journey-btn {
    border: 2px solid white !important;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.journey-btn:hover {
    transform: translateY(-5px) scale(1.05) !important;
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.25) !important;
}

.journey-btn-flat {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
}
.journey-btn-flat:hover {
    transform: scale(1.1) !important;
}

.fortune-item {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
}

.fortune-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 140px;
    height: 140px;
    background: radial-gradient(circle, rgba(255, 193, 7, 0.25) 0%, transparent 70%);
    border-radius: 50%;
    animation: pulse 2s ease-in-out infinite;
    pointer-events: none;
}

@keyframes pulse {
    0%, 100% {
        transform: translate(-50%, -50%) scale(1);
        opacity: 0.5;
    }
    50% {
        transform: translate(-50%, -50%) scale(1.1);
        opacity: 0.7;
    }
}

.fortune-image {
    position: relative;
    z-index: 1;
    filter: drop-shadow(0 4px 12px rgba(255, 193, 7, 0.3));
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.fortune-item:hover .fortune-image {
    transform: scale(1.05) rotate(5deg);
}

/* ===== MAIN CONTENT ===== */
.card--flex-toolbar {
    margin-top: -124px;
}

.section-title {
    font-size: 18px;
    font-weight: 400;
    animation: fadeInUp 0.6s ease-out 0.3s both;
}

/* ===== SERVICE CARDS (copié du premier design) ===== */
.service-card {
    position: relative;
    background: white;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
    overflow: hidden;
    height: 100%;
    display: flex;
    flex-direction: column;
}

/* Accent border that appears on hover */
.card-accent-border {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    opacity: 0;
    transition: opacity 0.4s;
}

.service-card:hover .card-accent-border {
    opacity: 1;
}

.card-gradient {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 0;
    transition: opacity 0.4s;
    pointer-events: none;
}

.service-card:hover .card-gradient {
    opacity: 1;
}

.icon-btn {
    position: relative;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
    overflow: visible !important;
}

.icon-btn .icon-display {
    position: relative;

    color: white !important;
}

.icon-btn i {
    color: #ffffff !important;
    position: relative;
    z-index: 10;
}

.icon-btn i::before {
    color: #ffffff !important;
}

.service-card:hover .icon-btn {
    transform: scale(1.08) rotate(5deg) !important;
}

/* Effets glow par couleur */
.service-card:hover .icon-btn.red::before {
    box-shadow: 0 0 30px 10px rgba(244, 67, 54, 0.5);
}

.service-card:hover .icon-btn.purple::before {
    box-shadow: 0 0 30px 10px rgba(156, 39, 176, 0.5);
}

.service-card:hover .icon-btn.orange::before {
    box-shadow: 0 0 30px 10px rgba(255, 152, 0, 0.5);
}

.service-card:hover .icon-btn.blue::before {
    box-shadow: 0 0 30px 10px rgba(33, 150, 243, 0.5);
}

.service-card:hover .icon-btn.green::before {
    box-shadow: 0 0 30px 10px rgba(76, 175, 80, 0.5);
}

/* ===== CARD CONTENT ===== */
.card-content {
    width: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
}

.card-title {
    font-size: 16px;
    line-height: 1.3;
}

.card-description {
    font-size: 12px;
    line-height: 1.5;
    flex: 1;
}

.card-footer {
    display: flex;
    align-items: center;
    padding-top: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    margin-top: 8px;
}

.card-link {
    font-size: 13px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: gap 0.3s;
}

.service-card:hover .card-link {
    gap: 10px;
}

.card-arrow-icon {
    font-size: 11px !important;
    display: inline-block;
    transition: transform 0.3s;
}

.service-card:hover .card-arrow-icon {
    transform: translateX(3px);
}

/* ===== MENU ===== */
.menu-list {
    border-radius: 4px !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.menu-item {
    transition: background 0.2s;
    font-size: 14px;
}

.menu-item:hover {
    background: rgba(0, 0, 0, 0.05) !important;
}

/* ===== ANIMATIONS ===== */
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideInDown {
    from {
        opacity: 0;
        transform: translateY(-15px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* ===== CAROUSEL WELCOME SLIDE ===== */
.carousel-welcome-slide {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 20px;
}

/* ===== RESPONSIVE DESIGN ===== */
/* Extra Small Devices (phones, less than 600px) */
@media (max-width: 599px) {
    .hero-section {
        min-height: 300px;
        padding: 24px 0;
    }

    .welcome-badge {
        font-size: 12px;
        padding: 6px 12px;
        max-width: 90%;
    }

    .credit-score-badge {
        font-size: 12px;
        padding: 8px 16px;
    }

    .hero-title {
        font-size: 16px;
        margin-bottom: 24px;
    }

    .journey-flow {
        margin-top: 16px;
    }

    .journey-btn,
    .journey-btn-flat {
        width: 48px !important;
        height: 48px !important;
        min-width: 48px !important;
    }

    .journey-btn i.fa-3x {
        font-size: 1.5rem !important;
    }

    .journey-btn-flat i.fa-2x {
        font-size: 1rem !important;
    }

    .fortune-image {
        height: 60px !important;
        width: 80px !important;
    }

    .fortune-glow {
        width: 100px;
        height: 100px;
    }

    .card--flex-toolbar {
        margin-top: -80px;
    }

    .section-title {
        font-size: 16px;
    }

    .service-card {
        margin-bottom: 8px;
    }

    .icon-btn {
        width: 48px !important;
        height: 48px !important;
    }

    .icon-btn i {
        font-size: 1.2rem !important;
    }

    .card-title {
        font-size: 14px !important;
    }

    .card-description {
        font-size: 11px !important;
    }

    .card-link {
        font-size: 12px;
    }
}

/* Small Devices (tablets, 600px to 959px) */
@media (min-width: 600px) and (max-width: 959px) {
    .hero-section {
        min-height: 340px;
        padding: 32px 0;
    }

    .welcome-badge {
        font-size: 13px;
    }

    .hero-title {
        font-size: 20px;
    }

    .journey-btn,
    .journey-btn-flat {
        width: 56px !important;
        height: 56px !important;
    }

    .journey-btn i.fa-3x {
        font-size: 1.8rem !important;
    }

    .journey-btn-flat i.fa-2x {
        font-size: 1.2rem !important;
    }

    .fortune-image {
        height: 75px !important;
        width: 100px !important;
    }

    .card--flex-toolbar {
        margin-top: -100px;
    }

    .section-title {
        font-size: 17px;
    }
}

/* Medium Devices (small laptops, 960px to 1263px) */
@media (min-width: 960px) and (max-width: 1263px) {
    .hero-section {
        min-height: 360px;
    }

    .card--flex-toolbar {
        margin-top: -124px;
    }
}

/* Large Devices (desktops, 1264px and up) */
@media (min-width: 1264px) {
    .hero-section {
        min-height: 380px;
    }

    .hero-title {
        font-size: 28px;
    }
}

/* Styles pour les overlays du carousel (conservés de votre design) */
.article-slide-overlay {
    position: relative;
    height: 100%;
    width: 100%;
    cursor: pointer;
    overflow: hidden;
}

.media-container-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

.article-background-image,
.article-background-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 0.3) 0%,
        rgba(0, 0, 0, 0.5) 50%,
        rgba(0, 0, 0, 0.7) 100%
    );
    z-index: 1;
}

.overlay-content {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 30px 40px;
    color: white;
    text-align: left;
    z-index: 2;
}

/* Styles des catégories (conservés de votre design) */
.category-card-pro {
    position: relative;
    height: 110px;
    border-radius: 14px;
    overflow: hidden;
    cursor: pointer;
    background: white;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    border: 1px solid rgba(0, 0, 0, 0.08);
}


.toolbar-animated {
    transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.toolbar-animated.expanded {
    animation: toolbarExpand 0.6s ease-out;
}

@keyframes toolbarExpand {
    0% {
        background: var(--v-primary-darken2);
    }
    50% {
        background: linear-gradient(135deg, #1976D2 0%, #1565C0 50%, #0D47A1 100%);
        box-shadow: 0 8px 32px rgba(25, 118, 210, 0.4);
    }
    100% {
        background: var(--v-primary-darken2);
    }
}

/* Animation de l'image/vidéo du produit */
.animated-media {
    animation: fadeInScale 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes fadeInScale {
    0% {
        opacity: 0;
        transform: scale(0.9);
    }
    100% {
        opacity: 1;
        transform: scale(1);
    }
}

/* Animation de l'overlay sombre */
.animated-overlay {
    animation: fadeInOverlay 0.6s ease-out 0.2s backwards;
}

@keyframes fadeInOverlay {
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
}

/* Animation du contenu (texte, badge, prix) */
.animated-content {
    animation: slideInContent 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s backwards;
}

@keyframes slideInContent {
    0% {
        opacity: 0;
        transform: translateY(30px);
    }
    100% {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Animation du badge "MOST BOUGHT" */
.animated-content .article-badge {
    animation: bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.5s backwards;
}

@keyframes bounceIn {
    0% {
        opacity: 0;
        transform: scale(0.3) translateY(-20px);
    }
    50% {
        transform: scale(1.05);
    }
    100% {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

/* Animation des détails (prix et stock) */
.animated-content .article-details {
    animation: slideInDetails 0.6s ease-out 0.6s backwards;
}

@keyframes slideInDetails {
    0% {
        opacity: 0;
        transform: translateX(-20px);
    }
    100% {
        opacity: 1;
        transform: translateX(0);
    }
}

/* Animation du bouton */
.animated-content .v-btn {
    animation: fadeInButton 0.6s ease-out 0.8s backwards;
}

@keyframes fadeInButton {
    0% {
        opacity: 0;
        transform: translateY(10px);
    }
    100% {
        opacity: 1;
        transform: translateY(0);
    }
}
/* ============================================
   ÉCLAIRCIR LE CAROUSEL
   ============================================ */

/* Éclaircir le fond du carousel pour les slides produits */
.article-slide-overlay .image-overlay {
    background: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 0.15) 0%,
        rgba(0, 0, 0, 0.25) 50%,
        rgba(0, 0, 0, 0.4) 100%
    ) !important;
}

/* Éclaircir encore plus au survol */
.article-slide-overlay:hover .image-overlay {
    background: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 0.2) 0%,
        rgba(0, 0, 0, 0.3) 50%,
        rgba(0, 0, 0, 0.5) 100%
    ) !important;
}

/* Ajouter une luminosité à l'image/vidéo */
.article-background-image,
.article-background-video {
    filter: brightness(1.2) contrast(1.05);
}

/* Ajouter une lueur derrière le contenu pour le rendre plus lisible */
.overlay-content {
    text-shadow:
        0 2px 8px rgba(0, 0, 0, 0.8),
        0 0 20px rgba(0, 0, 0, 0.6);
}

/* Éclaircir le fond général du carousel */
.header-carousel {
    background: rgba(255, 255, 255, 0.05) !important;
}

/* Ajouter une lueur subtile autour du carousel */
.toolbar-animated.expanded .header-carousel {
    box-shadow: inset 0 0 50px rgba(255, 255, 255, 0.1);
}

/* Optionnel : Ajouter un overlay lumineux */
.media-container-overlay::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at 50% 50%,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 70%
    );
    z-index: 1;
    pointer-events: none;
}
/* ============================================
   ANIMATION DE PLUIE VISIBLE ET PRONONCÉE
   ============================================ */

/* Assurer que le contenu reste au-dessus */
.v-toolbar__content,
.v-toolbar__extension {
    position: relative;
    z-index: 2;
}

/* Container de la pluie */
.rain-effect {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    overflow: hidden;
    z-index: 0;
    pointer-events: none;
    background: linear-gradient(
        180deg,
        rgba(7, 19, 28, 0.5) 0%,
        rgba(48, 84, 114, 0.4) 100%
    );
}

/* Style des gouttes - BEAUCOUP PLUS VISIBLE */
.drop {
    border: 0.5vmin solid transparent;
    border-bottom-color: rgba(171, 194, 233, 0.95);
    border-left-color: rgba(171, 194, 233, 0.3);
    position: absolute;
    top: -5vmin;
    width: 0;
    height: 0;
    animation: fall ease-in infinite;
    --angle: 88deg;
    filter: drop-shadow(0 0 3px rgba(171, 194, 233, 0.8));
}

/* Animation de chute des gouttes */
@keyframes fall {
    0% {
        transform: rotate(var(--angle)) translateX(0);
    }
    10% {
        transform: rotate(var(--angle)) translateX(10vh);
    }
    100% {
        transform: rotate(var(--angle)) translateX(calc(100vh + 5vmin));
    }
}

/* Effet d'éclair PLUS VISIBLE au début */
.toolbar-animated.expanded {
    animation: visibleLightning 0.6s ease-out;
}

@keyframes visibleLightning {
    0% {
        box-shadow: 0 0 0 rgba(255, 255, 255, 0);
    }
    20%, 40% {
        box-shadow: 0 0 60px rgba(171, 194, 233, 0.9),
                    inset 0 0 40px rgba(255, 255, 255, 0.4);
        background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0.2),
            rgba(171, 194, 233, 0.15)
        );
    }
    100% {
        box-shadow: 0 4px 24px rgba(25, 118, 210, 0.5);
        background: var(--v-primary-darken2);
    }
}

/* Lueur ambiante PLUS VISIBLE */
.rain-effect::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        ellipse at 50% 30%,
        rgba(171, 194, 233, 0.35) 0%,
        rgba(100, 150, 200, 0.15) 50%,
        transparent 80%
    );
    animation: strongGlow 3s ease-in-out infinite;
}

@keyframes strongGlow {
    0%, 100% {
        opacity: 0.8;
    }
    50% {
        opacity: 1;
    }
}

/* Effet de brouillard PLUS VISIBLE */
.rain-effect::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40%;
    background: linear-gradient(
        to top,
        rgba(171, 194, 233, 0.4) 0%,
        rgba(100, 150, 200, 0.2) 50%,
        transparent 100%
    );
    animation: mistFlow 8s ease-in-out infinite;
}

@keyframes mistFlow {
    0%, 100% {
        opacity: 0.6;
        transform: translateY(0);
    }
    50% {
        opacity: 0.9;
        transform: translateY(-10px);
    }
}

/* Gouttes EXTRA PRONONCÉES */
.drop:nth-child(5n) {
    border-bottom-color: rgba(255, 255, 255, 0.9);
    border-width: 0.7vmin;
    filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.9));
}

/* Gouttes moyennes */
.drop:nth-child(3n) {
    border-bottom-color: rgba(171, 194, 233, 0.95);
    border-width: 0.6vmin;
    --angle: 86deg;
}

/* Gouttes standard visibles */
.drop:nth-child(2n) {
    border-bottom-color: rgba(171, 194, 233, 0.85);
    --angle: 90deg;
}

/* Ajout de brillance aux gouttes */
.drop:nth-child(4n) {
    border-left-color: rgba(255, 255, 255, 0.5);
    filter: drop-shadow(0 0 4px rgba(171, 194, 233, 1));
}

/* Pulsation continue du toolbar */
.toolbar-animated.expanded {
    animation: visibleLightning 0.6s ease-out,
               continuousPulse 3s ease-in-out 0.6s infinite;
}

@keyframes continuousPulse {
    0%, 100% {
        box-shadow: 0 4px 24px rgba(25, 118, 210, 0.5);
    }
    50% {
        box-shadow: 0 6px 36px rgba(171, 194, 233, 0.7),
                    0 0 20px rgba(171, 194, 233, 0.3);
    }
}

/* Responsive - garder la visibilité */
@media (max-width: 959px) {
    .drop {
        border-width: 0.4vmin;
    }

    .drop:nth-child(5n) {
        border-width: 0.6vmin;
    }
}

@media (max-width: 599px) {
    .drop {
        border-width: 0.35vmin;
    }

    .drop:nth-child(5n) {
        border-width: 0.5vmin;
    }

    .rain-effect::before {
        opacity: 0.9;
    }
}
/* Styles du carousel */
.carousel-container {
    width: 100%;
    padding: 0 !important;
}

.header-carousel {
    width: 100%;
    border-radius: 0;
}

.carousel-slide-content {
    padding: 20px;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
}

.explore-text {
    margin-top: 15px;
    color: white;
}

/* Styles pour les slides d'articles */
.article-slide {
    height: 100%;
    padding: 20px 40px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.article-slide:hover {
    background: rgba(255, 255, 255, 0.05);
}

.article-image-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 180px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    overflow: hidden;
    object-fit: cover;
}

.article-carousel-image {
    border-radius: 12px;
}

.article-info {
    padding-left: 30px;
    text-align: left;
}

.article-badge {
    display: inline-block;
    background: rgba(255, 193, 7, 0.9);
    color: #1a237e;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: bold;
    margin-bottom: 10px;
    letter-spacing: 0.5px;
}

.article-title {
    color: white;
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 8px;
    text-shadow: 0 2px 4px rgba(0,0,0,0.3);
}

.article-description {
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 15px;
}

.article-details {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-top: 10px;
}

.price-tag {
    background: rgba(255, 255, 255, 0.15);
    padding: 8px 16px;
    border-radius: 8px;
    backdrop-filter: blur(10px);
}

.price-label {
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
    margin-right: 8px;
}

.price-value {
    color: white;
    font-size: 18px;
    font-weight: bold;
}

.stock-info {
    display: flex;
    align-items: center;
    gap: 6px;
    color: rgba(255, 255, 255, 0.9);
    font-size: 13px;
}

/* Responsive */
@media (max-width: 599px) {
    .article-slide {
        padding: 15px 20px;
    }

    .article-info {
        padding-left: 15px;
    }

    .article-title {
        font-size: 18px;
    }

    .article-description {
        font-size: 12px;
    }

    .saving_heading {
        font-size: 12px;
    }
}

/* Tes autres styles existants */
.outine-2 {
    border: 2px solid white;
}

    .card--flex-toolbar {
        margin-top: -124px;
    }

/* ============================================
   STYLES UX AVEC COULEURS VIVES
   ============================================ */

.category-card-pro {
    position: relative;
    height: 110px;
    border-radius: 14px;
    overflow: hidden;
    cursor: pointer;
    background: white;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    border: 1px solid rgba(0, 0, 0, 0.08);
}

.category-card-pro:hover {
    transform: translateY(-6px);
    border-color: transparent;
}

/* Gradient backgrounds - COULEURS VIVES */
.category-gradient {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.15;
    transition: opacity 0.4s ease;
    z-index: 1;
}

.category-gradient.active {
    opacity: 1;
}

/* Business - Violet vif */
.business-gradient {
    background: linear-gradient(135deg, #9C27B0 0%, #E91E63 100%);
}

/* Electronics - Bleu cyan vif */
.electronic-gradient {
    background: linear-gradient(135deg, #00BCD4 0%, #2196F3 100%);
}

/* Bills - Orange/Rouge vif */
.bills-gradient {
    background: linear-gradient(135deg, #FF5722 0%, #FF9800 100%);
}

/* Contenu de la carte */
.category-content-pro {
    position: relative;
    z-index: 2;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 16px 12px;
}

/* Wrapper de l'icône */
.icon-wrapper-pro {
    width: 52px;
    height: 52px;
    border-radius: 12px;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.icon-wrapper-pro.hover {
    transform: scale(1.15) rotate(5deg);
    background: white;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.category-icon-pro {
    font-size: 24px;
    transition: all 0.3s ease;
}

/* Couleurs des icônes - VIVES */
.category-business-pro .category-icon-pro {
    color: #9C27B0;
}

.category-electronic-pro .category-icon-pro {
    color: #00BCD4;
}

.category-bills-pro .category-icon-pro {
    color: #FF5722;
}

.category-business-pro:hover .category-icon-pro {
    color: white !important;
}

.category-electronic-pro:hover .category-icon-pro {
    color: white !important;
}

.category-bills-pro:hover .category-icon-pro {
    color: white !important;
}

/* Texte de la catégorie */
.category-text-pro {
    text-align: center;
    transition: all 0.3s ease;
}

.category-title-pro {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.8px;
    color: #424242;
    margin-bottom: 2px;
    transition: color 0.3s ease;
    text-transform: uppercase;
}

.category-subtitle-pro {
    font-size: 11px;
    font-weight: 500;
    color: #757575;
    letter-spacing: 0.3px;
    transition: color 0.3s ease;
}

.category-card-pro:hover .category-title-pro,
.category-card-pro:hover .category-subtitle-pro {
    color: white;
}

/* Flèche indicatrice */
.category-arrow-pro {
    position: absolute;
    bottom: 8px;
    right: 8px;
    opacity: 0;
    transform: translateX(-10px);
    transition: all 0.3s ease;
    color: white !important;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    padding: 4px;
}

.category-arrow-pro.visible {
    opacity: 1;
    transform: translateX(0);
}

/* Accent au bas de la carte - COULEURS VIVES */
.category-accent {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    height: 3px;
    width: 0;
    transition: width 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    z-index: 3;
}

.category-accent.expanded {
    width: 90%;
}

.business-accent {
    background: linear-gradient(90deg, #9C27B0 0%, #E91E63 100%);
}

.electronic-accent {
    background: linear-gradient(90deg, #00BCD4 0%, #2196F3 100%);
}

.bills-accent {
    background: linear-gradient(90deg, #FF5722 0%, #FF9800 100%);
}

/* Bouton "Explore All" */
.explore-all-btn {
    font-weight: 600;
    letter-spacing: 0.5px;
    padding: 0 28px !important;
    transition: all 0.3s ease;
    text-transform: none;
}

.explore-all-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(33, 150, 243, 0.4);
}

/* ============================================
   RESPONSIVE
   ============================================ */

/* Tablettes */
@media (max-width: 959px) and (min-width: 600px) {
    .category-card-pro {
        height: 105px;
    }

    .icon-wrapper-pro {
        width: 48px;
        height: 48px;
    }

    .category-icon-pro {
        font-size: 22px;
    }

    .category-title-pro {
        font-size: 13px;
    }

    .category-subtitle-pro {
        font-size: 10px;
    }
}

/* Mobiles */
@media (max-width: 599px) {
    .category-card-pro {
        height: 100px;
        border-radius: 12px;
    }

    .icon-wrapper-pro {
        width: 44px;
        height: 44px;
        margin-bottom: 8px;
    }

    .category-icon-pro {
        font-size: 20px;
    }

    .category-title-pro {
        font-size: 11px;
        letter-spacing: 0.5px;
    }

    .category-subtitle-pro {
        font-size: 9px;
        letter-spacing: 0.2px;
    }

    .category-content-pro {
        padding: 12px 8px;
    }

    .category-card-pro:hover {
        transform: translateY(-3px);
    }

    .icon-wrapper-pro.hover {
        transform: scale(1.1) rotate(3deg);
    }

    .category-arrow-pro {
        font-size: 18px;
        bottom: 6px;
        right: 6px;
    }

    .explore-all-btn {
        font-size: 13px;
        padding: 0 20px !important;
    }
}

/* Très petits mobiles */
@media (max-width: 360px) {
    .category-card-pro {
        height: 95px;
    }

    .icon-wrapper-pro {
        width: 40px;
        height: 40px;
    }

    .category-icon-pro {
        font-size: 18px;
    }

    .category-title-pro {
        font-size: 10px;
    }

    .category-subtitle-pro {
        font-size: 8px;
    }
}
.media-container video::-webkit-media-controls {
    opacity: 0.7;
}

.media-container video:hover::-webkit-media-controls {
    opacity: 1;
}

.v-card:hover .v-image {
    filter: brightness(1.05);
}

.v-card {
    cursor: pointer;
    transition: all 0.3s ease;
}

.v-card:hover {
    transform: translateY(-2px);
}
/* HEALTH CATEGORY */
.category-health-pro .category-icon-pro {
color: #4CAF50; /* vert santé */
}
.health-gradient {
background: linear-gradient(135deg, #66BB6A 0%, #388E3C 100%);
}
.health-accent {
background: linear-gradient(90deg, #66BB6A 0%, #388E3C 100%);
}
.category-health-pro:hover .category-icon-pro {
color: white !important;
}
/* Styles pour la superposition */
.article-slide-overlay {
position: relative;
height: 100%;
width: 100%;
cursor: pointer;
overflow: hidden;
}
.media-container-overlay {
position: absolute;
top: 0;
left: 0;
width: 100%;
height: 100%;
}
.article-background-image,
.article-background-video {
width: 100%;
height: 100%;
object-fit: cover;
}
.image-overlay {
position: absolute;
top: 0;
left: 0;
width: 100%;
height: 100%;
background: linear-gradient(
to bottom,
rgba(0, 0, 0, 0.3) 0%,
rgba(0, 0, 0, 0.5) 50%,
rgba(0, 0, 0, 0.7) 100%
);
z-index: 1;
}
.overlay-content {
position: absolute;
top: 0;
left: 0;
width: 100%;
height: 100%;
display: flex;
flex-direction: column;
justify-content: center;
padding: 30px 40px;
color: white;
text-align: left;
z-index: 2;
}
/* Ajustements des styles existants pour la superposition */
.overlay-content .article-badge {
background: rgba(255, 193, 7, 0.95);
color: #1a237e;
padding: 6px 16px;
border-radius: 20px;
font-size: 12px;
font-weight: bold;
margin-bottom: 15px;
align-self: flex-start;
letter-spacing: 0.8px;
}
.overlay-content .article-title {
color: white;
font-size: 28px;
font-weight: bold;
margin-bottom: 12px;
text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
line-height: 1.2;
}
.overlay-content .article-description {
color: rgba(255, 255, 255, 0.95);
font-size: 16px;
line-height: 1.6;
margin-bottom: 20px;
max-width: 80%;
text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
}
.overlay-content .article-details {
display: flex;
align-items: center;
gap: 25px;
margin-bottom: 20px;
}
.overlay-content .price-tag {
background: rgba(255, 255, 255, 0.2);
padding: 10px 20px;
border-radius: 10px;
backdrop-filter: blur(10px);
border: 1px solid rgba(255, 255, 255, 0.3);
}
.overlay-content .price-label {
color: rgba(255, 255, 255, 0.9);
font-size: 13px;
margin-right: 8px;
}
.overlay-content .price-value {
color: white;
font-size: 20px;
font-weight: bold;
}
.overlay-content .stock-info {
display: flex;
align-items: center;
gap: 8px;
color: rgba(255, 255, 255, 0.95);
font-size: 14px;
background: rgba(76, 175, 80, 0.8);
padding: 6px 12px;
border-radius: 8px;
}
/* Animation au survol */
.article-slide-overlay:hover .image-overlay {
background: linear-gradient(
to bottom,
rgba(0, 0, 0, 0.4) 0%,
rgba(0, 0, 0, 0.6) 50%,
rgba(0, 0, 0, 0.8) 100%
);
}
.article-slide-overlay:hover .overlay-content {
transform: translateY(-5px);
transition: transform 0.3s ease;
}
/* Responsive */
@media (max-width: 960px) {
.overlay-content {
padding: 20px 30px;
}
.overlay-content .article-title {
    font-size: 24px;
}

.overlay-content .article-description {
    font-size: 14px;
    max-width: 90%;
}
}
@media (max-width: 600px) {
.overlay-content {
padding: 15px 20px;
text-align: center;
}
.overlay-content .article-title {
    font-size: 20px;
}

.overlay-content .article-description {
    font-size: 13px;
    max-width: 100%;
}

.overlay-content .article-details {
    flex-direction: column;
    gap: 15px;
    align-items: center;
}

.overlay-content .article-badge {
    align-self: center;
}
}
.v-toolbar[extension-height="390"] ~ .container .card--flex-toolbar {
margin-top: -204px;
}
/* Styles responsifs pour les différentes hauteurs */
@media (max-width: 599px) {
.card--flex-toolbar {
margin-top: -100px;
}
.v-toolbar[extension-height="390"] ~ .container .card--flex-toolbar {
    margin-top: -180px;
}
}
/* Version ajustée - moins à gauche */
.carousel-container .v-carousel__item:first-child .carousel-slide-content {
    margin-left: -20px !important;
    padding-left: 25px !important;
    width: calc(100% + 20px) !important;
    transform: translateX(-8px) !important;
}

/* Ajuster le contenu texte */
.carousel-container .v-carousel__item:first-child .carousel-slide-content span,
.carousel-container .v-carousel__item:first-child .carousel-slide-content h2 {
    margin-left: -8px !important;
    padding-left: 5px !important;
}

/* Déplacer les boutons et icônes */
.carousel-container .v-carousel__item:first-child .v-layout {
    margin-left: -15px !important;
    transform: translateX(-5px) !important;
    padding-left: 10px !important;
}

/* Ajuster spécifiquement les boutons */
.carousel-container .v-carousel__item:first-child .v-btn {
    margin-left: -2px !important;
}

/* Ajuster l'image market_place_items */
.carousel-container .v-carousel__item:first-child .v-img {
    margin-left: -5px !important;
}

/* Version mobile ajustée */
@media (max-width: 959px) {
    .carousel-container .v-carousel__item:first-child .carousel-slide-content {
        margin-left: -15px !important;
        padding-left: 20px !important;
        width: calc(100% + 15px) !important;
        transform: translateX(-5px) !important;
    }
}

@media (max-width: 599px) {
    .carousel-container .v-carousel__item:first-child .carousel-slide-content {
        margin-left: -10px !important;
        padding-left: 15px !important;
        width: calc(100% + 10px) !important;
        transform: translateX(-3px) !important;
    }

    .carousel-container .v-carousel__item:first-child .v-layout {
        margin-left: -8px !important;
        transform: translateX(-2px) !important;
    }
}
/* Premier slide - texte proche des icônes */
.carousel-slide-content {
    padding: 20px;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    color: white;
}

.welcome-text {
    font-size: 16px;
    color: white;
    display: block;
    margin-bottom: 5px;
    /* Troncature avec ... sur 2 lignes */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    padding: 0 10px;
    line-height: 1.4;
}
.welcome-text.single-line {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: initial;
    -webkit-box-orient: initial;
     text-transform: initial;
  text-decoration: underline;
}
.saving_heading {
    color: white;
    margin-top: 5px;
    margin-bottom: 8px;
    font-weight: 400;
    /* Troncature avec ... sur 2 lignes */
    display: -webkit-box;
     -webkit-line-clamp: initial;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    padding: 0 10px;
    line-height: 1.3;
}

.explore-text {
    margin-top: 15px;
    color: white;
}

.outine-2 {
    border: 2px solid white;
}

@media (max-width: 599px) {
    .saving_heading {
        font-size: 12px;
        -webkit-line-clamp: 2;
    }

    .welcome-text {
        font-size: 14px;
        -webkit-line-clamp: 2;
    }

    .carousel-slide-content {
        padding: 15px 10px;
    }
}

@media (min-width: 800px) {
  .header-carousel .v-carousel__item:first-child .carousel-slide-content {
    padding-top: 10px !important;
  }
}

/* Styles pour les tiers de Credit Score */
.credit-score-tiers {
  margin-top: 20px;
}

.tier-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: 15px;
}

.tier-item {
  padding: 15px;
  border-radius: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

.tier-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.tier-name {
  font-weight: bold;
  margin-left: 8px;
  font-size: 1.1rem;
}

.tier-range {
  font-size: 0.9rem;
  margin-bottom: 5px;
  opacity: 0.9;
}

.tier-benefits {
  font-size: 0.8rem;
  opacity: 0.8;
  margin-top: 5px;
}

/* Classes de couleurs pour les tiers */
.bronze-tier {
  background: linear-gradient(135deg, #CD7F32 0%, #8B4513 100%);
  color: white;
}

.silver-tier {
  background: linear-gradient(135deg, #C0C0C0 0%, #808080 100%);
  color: white;
}

.gold-tier {
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  color: white;
}

.platinum-tier {
  background: linear-gradient(135deg, #E5E4E2 0%, #B9B6B2 100%);
  color: #333;
}

/* Styles pour les boutons et icônes du dialogue */
.credit-score-info-btn {
  margin-left: 4px;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.3), rgba(255, 165, 0, 0.3));
  border: 2px solid #FFD700;
  transition: all 0.3s ease;
  box-shadow: 0 0 10px rgba(255, 215, 0, 0.4);
}

.credit-score-info-btn:hover {
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.5), rgba(255, 165, 0, 0.5));
  transform: scale(1.15) rotate(5deg);
  box-shadow: 0 0 20px rgba(255, 215, 0, 0.6);
}

/* Animation pulse et glow pour le bouton */
.pulse-glow {
  animation: pulseGlow 2s infinite;
}

@keyframes pulseGlow {
  0%, 100% {
    box-shadow: 0 0 10px rgba(255, 215, 0, 0.4);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 20px rgba(255, 215, 0, 0.8), 0 0 30px rgba(255, 165, 0, 0.5);
    transform: scale(1.08);
  }
}

.credit-score-info-btn:hover.pulse-glow {
  animation: none;
}

.credit-score-text {
  display: inline-block;
  margin-right: 8px;
  background: linear-gradient(45deg, #FFD700, #FFA500);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: bold;
  font-size: 1.3rem;
}

.credit-score-section {
  margin-top: 8px;
}

/* Styles responsive pour le dialogue */
@media (max-width: 600px) {
  .brackets-table {
    font-size: 0.9rem;
  }

  .column {
    padding: 4px;
  }

  .table-header, .table-row {
    flex-direction: column;
  }

  .price-range, .required-credit, .example {
    flex: 1;
    margin-bottom: 5px;
  }

  .tier-list {
    grid-template-columns: 1fr;
  }

  .benefits-grid {
    grid-template-columns: 1fr;
  }

  .credit-score-text {
    font-size: 1.1rem;
  }
}

/* Progressif apparition des éléments */
.v-flex {
  opacity: 0;
  animation: fadeInUp 0.5s ease forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

</style>
